import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { formatQuizAnswer } from '../../utils/formatQuizAnswer';
import { Col, Card, CardBody, Container, Row } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { Btn } from '../../AbstractElements';
import { ToastContainer, toast } from 'react-toastify';
import {
  useGetOrdersQuery,
  useOrderDetailsMutation,
  useGetIngredientsCodesQuery,
  useSendEmailUpdatesMutation,
  useUpdateOrderStatusAndDetailsMutation,
  useAssignBatchMutation,
} from '../../features/ordersApiSlice';
import { useMarkUserNoteCompleteMutation } from '../../features/users/userApiSlice';
import {
  downloadMultipleOrderPdfs,
  generateOrderPdfBuffer,
  generateLabelPdfBuffer,
  downloadCsv,
  extractTrackingId,
} from '../../utils';
import { useGetIngredientPricesQuery } from '../../features/ordersApiSlice';
import { useGetAssetsQuery } from '../../features/assetApiSlice';
import { useNavigate } from 'react-router';
import { producedDays, monthsFromRiiseDuration, isOmega3OnlyRiise } from '../../utils/labelDays';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';

const formatOrderItemsInline = (orderItems = []) => {
  if (!Array.isArray(orderItems) || orderItems.length === 0) return '-';
  return orderItems
    .map((item) => item?.product_name || item?.product_type || '-')
    .filter(Boolean)
    .join(', ');
};

const OrderItemsExpanded = ({ data }) => {
  const items = Array.isArray(data?.order_items) ? data.order_items : [];
  if (!items.length) return null;

  return (
    <div className="px-4 py-3 bg-light border-top">
      <div className="mb-3">
        <div className="fw-semibold text-dark">Order items</div>
        <div className="text-muted small">Detailed line items for order #{data?.id || '-'}</div>
      </div>
      <div className="table-responsive">
        <table className="table table-sm table-bordered align-middle mb-0" style={{ backgroundColor: "#ffffff", color: "#212529" }}>
          <thead style={{ backgroundColor: "#f8f9fa", color: "#212529" }}>
            <tr>
              <th style={{ color: "#212529", fontWeight: 600 }}>Order ID</th>
              <th style={{ color: "#212529", fontWeight: 600 }}>Item ID</th>
              <th style={{ color: "#212529", fontWeight: 600 }}>Product</th>
              <th style={{ color: "#212529", fontWeight: 600 }}>Product Type</th>
              <th style={{ color: "#212529", fontWeight: 600 }}>Line</th>
              <th style={{ color: "#212529", fontWeight: 600 }}>Price</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id || `${item.order_id}-${item.line_item}`} style={{ color: "#212529" }}>
                <td>{item.order_id || data?.id || '-'}</td>
                <td>{item.id || '-'}</td>
                <td>{item.product_name || '-'}</td>
                <td>{item.product_type || '-'}</td>
                <td>{item.line_item || '-'}</td>
                <td>{item.price || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const OrdersPdf = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [ingredients, setIngredients] = useState([]);
  const [toggleSelect] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [editingCell, setEditingCell] = useState(null); // { rowId, field }
  const [editedData, setEditedData] = useState({}); // Store edited values
  const [editedIngredients, setEditedIngredients] = useState({}); // Store edited ingredient quantities for all orders
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isDownloadingLabel, setIsDownloadingLabel] = useState(false);
  const [isDownloadingSummary, setIsDownloadingSummary] = useState(false);
  const [markingCompleteNoteId, setMarkingCompleteNoteId] = useState(null);
  const [updateStatusModalOpen, setUpdateStatusModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusDetails, setStatusDetails] = useState('');
  const [emailTemplateModalOpen, setEmailTemplateModalOpen] = useState(false);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState('track-shipment');

  const EMAIL_TEMPLATES = [
    { value: 'track-shipment', label: 'Shipment Update' },
    { value: 'package-returned', label: 'Package Returned' },
  ];

  const [getOrderDetails] = useOrderDetailsMutation();
  const [sendEmailUpdates, { isLoading: sendEmailUpdatesLoading }] = useSendEmailUpdatesMutation();
  const [updateOrderStatusAndDetails, { isLoading: updateOrderStatusAndDetailsLoading }] = useUpdateOrderStatusAndDetailsMutation();
  const [markUserNoteComplete] = useMarkUserNoteCompleteMutation();
  const [assignBatch] = useAssignBatchMutation();
  // Get required data for PDF generation
  const { data: ingredientPrices } = useGetIngredientPricesQuery('en');
  const { data: focusAreas } = useGetAssetsQuery('focusArea');
  const { data: allIngredientCodes } = useGetIngredientsCodesQuery();

  const [order, setOrder] = useState({
    orderNo: '',
    date: '',
    first_name: '',
    last_name: '',
    totalPrice: '',
    status: '',
    duration: '',
    transactionId: '',
    paymentMethod: '',
    paymentStatus: '',
    amount: '',
    customer_quiz_id: 1,
  });

  const [questionsAnswers, setQuestionAnswers] = useState();
  const [personalInfo, setPersonalInfo] = useState();
  const [shippingAddress, setShippingAddress] = useState();

  // Debounce search term to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page when searching
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: fetchOrders,
    isLoading,
    isSuccess,
    refetch,
  } = useGetOrdersQuery(
    {
      page: currentPage,
      limit: pageSize,
      search: debouncedSearchTerm,
      status: statusFilter,
    },
    { skip: false }
  );
  console.log('selectedRows => ', selectedRows);

  useEffect(() => {
    refetch();
  }, [refetch, currentPage, pageSize, debouncedSearchTerm, statusFilter]);

  useEffect(() => {
    const fn = () => {
      if (isSuccess && fetchOrders?.data) {
        setData(fetchOrders.data?.data || []);
        setTotalRows(fetchOrders.data?.pagination?.total || 0);
      }
    };
    fn();
  }, [fetchOrders]);

  const handleRowSelected = useCallback((state) => {
    setSelectedRows(state.selectedRows);
  }, []);

  const handleCellEdit = (rowId, field, value) => {
    setEditedData((prev) => {
      const newData = {
        ...prev,
        [rowId]: {
          ...prev[rowId],
          [field]: value,
        },
      };
      return newData;
    });
  };

  const startEditing = (rowId, field) => {
    setEditingCell({ rowId, field });
  };

  const stopEditing = () => {
    setEditingCell(null);
  };

  const getDisplayValue = (row, field) => {
    const editedValue = editedData[row.id]?.[field];
    if (editedValue !== undefined) {
      return editedValue;
    }

    switch (field) {
      case 'name':
        return `${row.first_name} ${row.last_name}`;
      case 'subscription':
        return row.duration;
      case 'country':
        return row.country;
      default:
        return row[field];
    }
  };

  const handleIngredientEdit = (ingredientId, value) => {
    // Get current order ID to store edits per order
    const currentOrderId = order?.id;

    setEditedIngredients((prev) => ({
      ...prev,
      [currentOrderId]: {
        ...prev[currentOrderId],
        [ingredientId]: value,
      },
    }));

    // Recalculate NRV percentage when quantity changes
    if (allIngredientCodes?.data) {
      const ingredient = ingredients?.find((i) => i.id === ingredientId);
      if (ingredient) {
        const ingredientData = allIngredientCodes?.data?.find((i) => i?.code === ingredient?.ingredient_id);

        const euNrv = parseFloat(ingredientData?.eu_nrv);
        const newQuantity = parseFloat(value);

        if (euNrv > 0 && !isNaN(newQuantity)) {
          const newPerEuNrv = parseInt((newQuantity / euNrv) * 100);

          // Update the ingredients state with new NRV percentage
          setIngredients((prev) =>
            prev.map((item) => (item.id === ingredientId ? { ...item, per_eu_nrv: newPerEuNrv } : item))
          );
        }
      }
    }
  };

  const downloadAllSelectedOrders = async () => {
    if (!selectedRows?.length) {
      toast.error('Please select at least one order to download');
      return;
    }

    if (!ingredientPrices || !focusAreas) {
      toast.error('Required data is still loading. Please wait and try again.');
      return;
    }

    const loadingToast = toast.loading('Generating PDFs...');
    try {
      setIsDownloadingPdf(true);
      // Show loading toast

      // Fetch all order details
      const promises = selectedRows.map((row) => getOrderDetails({ order_id: row.id }).unwrap());
      const allOrdersData = await Promise.all(promises);

      // Generate PDF for each order
      const generateOrderPdf = async (order) => {
        // Create deep copy of the order data
        const mergedOrder = JSON.parse(JSON.stringify(order));

        // Get the order ID from the data array
        const orderId = order?.data?.[0]?.id;

        if (orderId) {
          // Find the corresponding table row to get edited data
          const tableRow = selectedRows.find((row) => row.id === orderId);
          if (tableRow) {
            // Merge edited name, subscription, country data using table row ID
            if (editedData[tableRow.id]) {
              if (editedData[tableRow.id].name) {
                // Split the edited name into first and last name
                const nameParts = editedData[tableRow.id].name.split(' ');
                const firstName = nameParts[0] || '';
                const lastName = nameParts.slice(1).join(' ') || '';
                mergedOrder.data[0] = {
                  ...mergedOrder.data[0],
                  first_name: firstName,
                  last_name: lastName,
                };
              }

              if (editedData[tableRow.id].subscription) {
                mergedOrder.data[0] = {
                  ...mergedOrder.data[0],
                  duration: editedData[tableRow.id].subscription,
                };
              }

              if (editedData[tableRow.id].country) {
                // Update shipping address country if it exists
                if (mergedOrder.shippingAddress && mergedOrder.shippingAddress[0]) {
                  mergedOrder.shippingAddress[0] = {
                    ...mergedOrder.shippingAddress[0],
                    country: editedData[tableRow.id].country,
                  };
                }
              }
            }

            // Merge edited ingredients data for this specific order
            const orderIngredientEdits = editedIngredients[tableRow.id];
            if (orderIngredientEdits && Object.keys(orderIngredientEdits).length > 0) {
              mergedOrder.ingredients = mergedOrder.ingredients?.map((ingredient) => {
                const editedQuantity = orderIngredientEdits[ingredient.id];
                if (editedQuantity !== undefined) {
                  return {
                    ...ingredient,
                    daily_dose: editedQuantity,
                  };
                }
                return ingredient;
              });
            }
          }
        }

        return await generateOrderPdfBuffer(mergedOrder, ingredientPrices, focusAreas);
      };

      // Download all PDFs as a single file
      await downloadMultipleOrderPdfs(allOrdersData, generateOrderPdf);

      // Close loading toast and show success
      toast.dismiss(loadingToast);
      toast.success(`Successfully downloaded ${selectedRows.length} order PDF(s)`);
    } catch (error) {
      console.error('Error downloading orders:', error);
      toast.error('Error generating PDFs. Please try again.');
    } finally {
      toast.dismiss(loadingToast);
      setIsDownloadingPdf(false);
    }
  };

  const downloadAllSelectedLabels = async () => {
    if (!selectedRows?.length) {
      toast.error('Please select at least one order to download labels');
      return;
    }

    if (!ingredientPrices) {
      toast.error('Required data is still loading. Please wait and try again.');
      return;
    }

    // Prompt for batch number
    const input = window.prompt('Enter batch number (e.g. 123):');
    if (!input || !input.trim()) return;
    const batch = 'PROD' + input.trim();

    // Assign batch on the server
    try {
      await assignBatch({
        orderIds: selectedRows.map((row) => row.id),
        batch,
        type: 'web',
      }).unwrap();
    } catch (error) {
      console.error('Error assigning batch:', error);
      toast.error('Error assigning batch. Please try again.');
      return;
    }

    const loadingToast = toast.loading('Generating Labels...');
    try {
      setIsDownloadingLabel(true);

      // SHI-528 — an Omega-3-only order has no dosed pouches, so it has no ingredient label.
      // Printing one produced a blank page with a 0g net quantity that had to be pulled out
      // of the stack by hand: 20 selected with 1 Omega-3-only now yields 19 pages.
      // Filtered on the rows, before fetching details — same test the pouch column uses, so
      // what you see in the list is what you get in the PDF, and we skip the fetch too.
      const printableRows = selectedRows.filter((row) => !isOmega3OnlyRiise(row.order_items));
      const skipped = selectedRows.length - printableRows.length;

      if (!printableRows.length) {
        toast.dismiss(loadingToast);
        toast.error('Nothing to print — every selected order is Omega-3 only, which has no ingredient label.');
        setIsDownloadingLabel(false);
        return;
      }

      const promises = printableRows.map((row) => getOrderDetails({ order_id: row.id }).unwrap());
      const allOrdersData = await Promise.all(promises);

      const generateLabelPdf = async (order) => {
        const mergedOrder = JSON.parse(JSON.stringify(order));

        const orderId = order?.data?.[0]?.id;

        if (orderId) {
          const tableRow = selectedRows.find((row) => row.id === orderId);
          if (tableRow) {
            if (editedData[tableRow.id]) {
              if (editedData[tableRow.id].name) {
                const nameParts = editedData[tableRow.id].name.split(' ');
                const firstName = nameParts[0] || '';
                const lastName = nameParts.slice(1).join(' ') || '';
                mergedOrder.data[0] = {
                  ...mergedOrder.data[0],
                  first_name: firstName,
                  last_name: lastName,
                };
              }

              if (editedData[tableRow.id].subscription) {
                mergedOrder.data[0] = {
                  ...mergedOrder.data[0],
                  duration: editedData[tableRow.id].subscription,
                };
              }

              if (editedData[tableRow.id].country) {
                if (mergedOrder.shippingAddress && mergedOrder.shippingAddress[0]) {
                  mergedOrder.shippingAddress[0] = {
                    ...mergedOrder.shippingAddress[0],
                    country: editedData[tableRow.id].country,
                  };
                }
              }
            }

            const orderIngredientEdits = editedIngredients[tableRow.id];
            if (orderIngredientEdits && Object.keys(orderIngredientEdits).length > 0) {
              mergedOrder.ingredients = mergedOrder.ingredients?.map((ingredient) => {
                const editedQuantity = orderIngredientEdits[ingredient.id];
                if (editedQuantity !== undefined) {
                  return {
                    ...ingredient,
                    daily_dose: editedQuantity,
                  };
                }
                return ingredient;
              });
            }
          }
        }

        return await generateLabelPdfBuffer(mergedOrder, ingredientPrices);
      };

      await downloadMultipleOrderPdfs(allOrdersData, generateLabelPdf, `Ingredients Labels riise - ${batch}.pdf`);

      toast.dismiss(loadingToast);
      toast.success(
        `Successfully downloaded ${allOrdersData.length} label PDF(s)`
        + (skipped ? ` — skipped ${skipped} Omega-3-only order${skipped === 1 ? '' : 's'} (no pouches)` : ''),
      );
    } catch (error) {
      console.error('Error downloading labels:', error);
      toast.error('Error generating label PDFs. Please try again.');
    } finally {
      toast.dismiss(loadingToast);
      setIsDownloadingLabel(false);
    }
  };

  const downloadWeightCalculationsCSV = async () => {
    if (!selectedRows?.length) {
      toast.error('Please select at least one order to download CSV');
      return;
    }

    try {
      setIsDownloadingCsv(true);
      const loadingToast = toast.loading('Generating CSV...');

      const promises = selectedRows.map((row) => getOrderDetails({ order_id: row.id }).unwrap());
      const allOrdersData = await Promise.all(promises);

      const allIngredients = new Set();
      allOrdersData.forEach((order) => {
        order.ingredients?.forEach((ingredient) => {
          allIngredients.add(ingredient.ingredient);
        });
      });

      const csvData = [];
      const headers = ['Ingredient'];

      allOrdersData.forEach((order) => {
        const orderId = order?.data?.[0]?.id;
        if (orderId) {
          headers.push(`Order ${orderId}`);
        }
      });

      Array.from(allIngredients).forEach((ingredientName) => {
        const row = { Ingredient: ingredientName };

        allOrdersData.forEach((order) => {
          const orderId = order?.data?.[0]?.id;
          const duration = order?.data?.[0]?.duration;
          // SHI-528 — duration is a PLAN CODE, not a month count. `=== '6' ? 93 : 31` sent
          // the 90-day one-off (duration 90, three months to prepare) down the 31-day
          // branch, so its raw weights came out a third of what they should be.
          const days = producedDays(monthsFromRiiseDuration(duration), null);
          const ingredient = order.ingredients?.find((ing) => ing.ingredient === ingredientName);

          if (ingredient) {
            let quantity = ingredient.daily_dose;
            if (editedIngredients[orderId]?.[ingredient.id]) {
              quantity = editedIngredients[orderId][ingredient.id];
            }
            const price = ingredientPrices?.data?.find(
              (item) => item?.ingredient_id === ingredient?.ingredient_id
            );
            const activePercentage = price?.active_percentage || 100;
            const weight = (parseFloat(quantity) * days) / activePercentage;
            row[`Order ${orderId}`] = Math.round(weight);
          } else {
            row[`Order ${orderId}`] = '0.00';
          }
        });

        csvData.push(row);
      });
      downloadCsv(csvData, headers);
      toast.dismiss(loadingToast);
      toast.success(`Successfully downloaded weight calculations CSV`);
    } catch (error) {
      console.error('Error generating CSV:', error);
      toast.error('Error generating CSV. Please try again.');
    } finally {
      setIsDownloadingCsv(false);
    }
  };

  const downloadSummaryOrdersCSV = async () => {
    if (!selectedRows?.length) {
      toast.error('Please select at least one order to download summary CSV');
      return;
    }

    try {
      setIsDownloadingSummary(true);
      const loadingToast = toast.loading('Generating Summary CSV...');

      const promises = selectedRows.map((row) => getOrderDetails({ order_id: row.id }).unwrap());
      const allOrdersData = await Promise.all(promises);

      const csvData = [];

      allOrdersData.forEach((order, index) => {
        const orderId = order?.data?.[0]?.id;
        const duration = order?.data?.[0]?.duration;
        const quizId = order?.data?.[0]?.customer_quiz_id;
        const isFirstOrder = order?.data?.[0]?.is_first_order === 1 ? 'Y' : 'N';
        // SHI-528 — duration is a PLAN CODE. See the recipe CSV above: the 90-day one-off
        // was being prepared as one month here.
        const monthsToPrepare = monthsFromRiiseDuration(duration);
        const days = producedDays(monthsToPrepare, null);
        const dailyPouches = days;
        const months = `${monthsToPrepare} month${monthsToPrepare === 1 ? '' : 's'}`;

        // Get edited name if available
        const tableRow = selectedRows.find((row) => row.id === orderId);
        let customerName = `${order?.data?.[0]?.first_name} ${order?.data?.[0]?.last_name}`;
        if (tableRow && editedData[tableRow.id]?.name) {
          customerName = editedData[tableRow.id].name;
        }

        // Calculate total weight per daily pouch
        let totalWeight = 0;
        order.ingredients?.forEach((ingredient) => {
          let quantity = ingredient.daily_dose;
          if (editedIngredients[orderId]?.[ingredient.id]) {
            quantity = editedIngredients[orderId][ingredient.id];
          }
          const price = ingredientPrices?.data?.find(
            (item) => item?.ingredient_id === ingredient?.ingredient_id
          );
          const activePercentage = price?.active_percentage || 100;
          const weight = (parseFloat(quantity) * days) / activePercentage;
          console.log('INGREDIENT => ', price?.name, weight);
          totalWeight += weight;
        });

        const weightPerDailyPouch = totalWeight / days;

        csvData.push({
          '#': index + 1,
          'Order id': orderId,
          Plan: duration,
          'Welcome pack (Y/N)': isFirstOrder,
          'Weight per daily pouch': Math.round(weightPerDailyPouch * 100) / 100,
          '#months to prepare': monthsToPrepare,
          '#daily pouches': dailyPouches,
          name: customerName,
          quiz_id: quizId,
          month: months,
        });
      });

      const headers = [
        '#',
        'Order id',
        'Plan',
        'Welcome pack (Y/N)',
        'Weight per daily pouch',
        '#months to prepare',
        '#daily pouches',
        'name',
        'quiz_id',
        'month',
      ];

      downloadCsv(csvData, headers);
      toast.dismiss(loadingToast);
      toast.success(`Successfully downloaded summary orders CSV`);
    } catch (error) {
      console.error('Error generating summary CSV:', error);
      toast.error('Error generating summary CSV. Please try again.');
    } finally {
      setIsDownloadingSummary(false);
    }
  };

  const handleAction = async (row) => {
    try {
      const res = await getOrderDetails({ order_id: row.id }).unwrap();
      setOrder(res.data[0]);
      setIngredients(res.ingredients);
      setQuestionAnswers(res.questionsAnswers);
      setPersonalInfo(res.personalInfo?.[0]);
      setShippingAddress(res.shippingAddress?.[0]);
    } catch (e) {
      console.log(e);
    }
  };

  const handleMarkOrderNoteComplete = async (noteId) => {
    setMarkingCompleteNoteId(noteId);
    try {
      await markUserNoteComplete(noteId).unwrap();
      toast.success('Note marked as complete.');

      // Update the order notes in the detail view
      setOrder((prev) => ({
        ...prev,
        notes: prev.notes?.map((note) => (note.id === noteId ? { ...note, isCompleted: 1 } : note)),
      }));

      // Update the table data to reflect the change for all orders with the same user email
      setData((prevData) =>
        prevData.map((row) => {
          if (row.email === shippingAddress?.email) {
            return {
              ...row,
              notes: row.notes?.map((note) => (note.id === noteId ? { ...note, isCompleted: 1 } : note)),
            };
          }
          return row;
        })
      );
    } catch (err) {
      console.log(err);
      toast.error('Error marking note as complete.');
    } finally {
      setMarkingCompleteNoteId(null);
    }
  };

  const tableColumns = [
    {
      name: 'Order#',
      selector: (row) => `${row.id}`,
      width: '80px',
    },
    {
      name: 'Notes',
      cell: (row) => {
        const incompleteNotes = row?.notes?.filter((note) => note.isCompleted === 0) || [];
        if (incompleteNotes.length === 0) return '-';
        return (
          <ol className="t-flex t-flex-col t-gap-1 t-list-disc">
            {incompleteNotes.map((note) => (
              <li key={note.id}>{note.note}</li>
            ))}
          </ol>
        );
      },
      sortable: false,
      width: '170px',
    },
    {
      name: 'Invoice Date',
      selector: (row) => (row['invoice_date'] ? row['invoice_date'] : row['createdAt']),
      sortable: true,
      center: false,
      width: '160px',
    },
    {
      name: 'Name',
      cell: (row) => {
        const isEditing = editingCell?.rowId === row.id && editingCell?.field === 'name';
        const displayValue = getDisplayValue(row, 'name');

        if (isEditing) {
          return (
            <input
              type="text"
              value={editedData[row.id]?.name || displayValue}
              onChange={(e) => handleCellEdit(row.id, 'name', e.target.value)}
              onBlur={stopEditing}
              onKeyPress={(e) => e.key === 'Enter' && stopEditing()}
              className="form-control form-control-sm"
              autoFocus
            />
          );
        }

        return (
          <div
            onClick={() => startEditing(row.id, 'name')}
            className="cursor-pointer hover:bg-gray-100 p-1 rounded"
            title="Click to edit"
          >
            {displayValue}
            {editedData[row.id]?.name && <span className="ml-1 text-xs text-blue-600">●</span>}
          </div>
        );
      },
      sortable: true,
      center: false,
      headerStyle: (selector, id) => {
        return { textAlign: 'left' };
      },
    },
    {
      name: 'Email',
      selector: (row) => row['email'],
      sortable: true,
      center: false,
      width: '160px',
    },
    // {
    //   name: 'Preparation Date',
    //   selector: (row) => row['preparation_date'],
    //   sortable: true,
    //   center: false,
    //   width: '160px',
    // },
    {
      name: 'Status',
      selector: (row) => row['status'],
      sortable: true,
      center: false,
    },
    {
      name: 'Subscription',
      cell: (row) => {
        const isEditing = editingCell?.rowId === row.id && editingCell?.field === 'subscription';
        const displayValue = getDisplayValue(row, 'subscription');

        if (isEditing) {
          return (
            <input
              type="text"
              value={editedData[row.id]?.subscription || displayValue}
              onChange={(e) => handleCellEdit(row.id, 'subscription', e.target.value)}
              onBlur={stopEditing}
              onKeyPress={(e) => e.key === 'Enter' && stopEditing()}
              className="form-control form-control-sm"
              autoFocus
            />
          );
        }

        return (
          <div
            onClick={() => startEditing(row.id, 'subscription')}
            className="cursor-pointer hover:bg-gray-100 p-1 rounded"
            title="Click to edit"
          >
            {displayValue}
            {editedData[row.id]?.subscription && <span className="ml-1 text-xs text-blue-600">●</span>}
            {/* SHI-528 — the real pouch count, so whoever prints labels sees what the bag
                actually holds. The riise value is a PLAN CODE, not a month count: 6 and 90
                are both 93 pouches, which reading "90" as months would get badly wrong.
                An Omega-3-only order has no dosed pouches at all, so it shows nothing. */}
            {!isOmega3OnlyRiise(row.order_items) && (
              <div style={{ fontSize: '11px', color: '#6b7280', lineHeight: 1.2 }}>
                {producedDays(monthsFromRiiseDuration(displayValue), null)} pouches
              </div>
            )}
          </div>
        );
      },
      sortable: true,
      center: false,
    },
    {
      name: 'Items',
      cell: (row) => formatOrderItemsInline(row.order_items),
      sortable: false,
      center: false,
      grow: 1.2,
      wrap: true,
    },
    {
      name: 'Amount',
      selector: (row) => row['totalPrice'],
      sortable: true,
      center: false,
    },
    {
      name: 'Country',
      cell: (row) => {
        const isEditing = editingCell?.rowId === row.id && editingCell?.field === 'country';
        const displayValue = getDisplayValue(row, 'country');

        if (isEditing) {
          return (
            <input
              type="text"
              value={editedData[row.id]?.country || displayValue}
              onChange={(e) => handleCellEdit(row.id, 'country', e.target.value)}
              onBlur={stopEditing}
              onKeyPress={(e) => e.key === 'Enter' && stopEditing()}
              className="form-control form-control-sm"
              autoFocus
            />
          );
        }

        return (
          <div
            onClick={() => startEditing(row.id, 'country')}
            className="cursor-pointer hover:bg-gray-100 p-1 rounded"
            title="Click to edit"
          >
            {displayValue}
            {editedData[row.id]?.country && <span className="ml-1 text-xs text-blue-600">●</span>}
          </div>
        );
      },
      sortable: true,
      center: false,
    },
    {
      name: 'Email Count',
      selector: (row) => row['email_count'],
      sortable: true,
      center: false,
    },
    {
      name: 'Action',
      width: '140px',
      cell: (row) => (
        <Row className="flex flex-row">
          <Btn attrBtn={{ color: 'primary', onClick: () => handleAction(row) }} className="col-6">
            {'View'}
          </Btn>
          {/* <Btn
            attrBtn={{ color: 'primary', onClick: () => navigate(`/admin/orders-pdf/${row.id}`) }}
            className="col-6"
          >
            Details
          </Btn> */}
        </Row>
      ),
    },
  ];

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
  };

  const handleStatus = (event) => {
    const value = event.target.value;
    setStatusFilter(value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPageSize(newPerPage);
    setCurrentPage(page);
  };

  const createShipment = async () => {
    // SHI-528 — never navigate with a URL the next page cannot read. With nothing selected
    // this built "?orderIds=undefined" (JSON.stringify(undefined) is the STRING
    // "undefined"), and ShipmentLabels' JSON.parse threw into a catch that only
    // console.logged — so the operator got a blank table and no reason for it.
    const orderIds = (selectedRows || []).map((item) => item?.id).filter((id) => id != null);
    if (!orderIds.length) {
      toast.error('Please select at least one order to create labels.');
      return;
    }
    navigate(`/admin/orders-pdf/shipment-labels?orderIds=${JSON.stringify(orderIds)}`);
  };

  const getShipmentTracking = async () => {
    if (!selectedRows?.length) {
      toast.error('Please select at least one order to download summary CSV');
      return;
    }
    const ordersWithTrackingId = selectedRows?.filter((item) => !!item?.details);
    if (ordersWithTrackingId?.length === 0) {
      toast.error('No order with tracking id');
      return;
    }
    console.log('ordersWithTrackingId => ', ordersWithTrackingId);
    const trackingIds = ordersWithTrackingId?.map((item) => extractTrackingId(item?.details));
    console.log('trackingIds => ', trackingIds);
    navigate(`/admin/orders-pdf/shipment-tracking?trackingIds=${JSON.stringify(trackingIds)}`, {
      state: {
        ordersWithTrackingId,
      },
    });
  };

  const toggleEmailTemplateModal = () => {
    if (selectedRows?.length === 0) {
      toast.error('Please select orders to send emails!');
      return;
    }
    setEmailTemplateModalOpen((prev) => !prev);
    if (!emailTemplateModalOpen) {
      setSelectedEmailTemplate('track-shipment');
    }
  };

  const sendEmailUpdatesSubmit = async () => {
    try {
      const loadingToast = toast.loading('Sending emails...');
      const orderIds = selectedRows?.map((item) => item?.id);
      const res = await sendEmailUpdates({ orderIds, template: selectedEmailTemplate });
      toast.dismiss(loadingToast);
      if (res?.error?.data) {
        toast.error(res?.error?.data?.message || 'Something went wrong!');
      } else {
        toast.success('Emails sent successfully!');
        setEmailTemplateModalOpen(false);
      }
    } catch (err) {
      toast.error(err?.message || `Something went wrong`);
      console.log('Error => ', err);
    }
  };

  const toggleUpdateStatusModal = () => {
    const isOpening = !updateStatusModalOpen;
    setUpdateStatusModalOpen(!updateStatusModalOpen);
    if (isOpening) {
      setSelectedStatus('');
      setStatusDetails('');
    }
  };

  const handleConfirmUpdateStatus = async () => {
    if (!selectedRows?.length) return;
    if (!selectedStatus) {
      toast.error('Please select a status');
      return;
    }
    try {
      const loadingToast = toast.loading('Updating order statuses...');
      const orderIds = selectedRows.map((row) => row.id);
      
      await updateOrderStatusAndDetails({ 
        orderIds,
        status: selectedStatus,
        details: statusDetails,
      }).unwrap();
      
      toast.dismiss(loadingToast);
      toast.success(`Status updated successfully for ${orderIds.length} order(s).`);
      toggleUpdateStatusModal();
      refetch();
    } catch (err) {
      toast.dismiss();
      toast.error(err?.data?.message || err?.message || 'Failed to update order statuses. Please try again.');
    }
  };

  return (
    <Fragment>
      <h1>Orders</h1>

      <Container fluid={true}>
        <Card>
          <CardBody>
            <Row>
              <div className="col-8">
                <input
                  type="text"
                  placeholder="Search by order ID, name, email, or status"
                  value={searchTerm}
                  onChange={handleSearch}
                  className="form-control col-8"
                />
              </div>
              <div className="col-4">
                <select className="form-control col-4" onChange={handleStatus}>
                  <option value="">Select status</option>
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="Ready to dispatch">Ready to dispatch</option>
                  <option value="dispatched">Dispatched</option>
                  <option value="delivered">Delivered</option>
                  <option value="failure">Failure</option>
                  <option value="pre-transit">Pre transit</option>
                  <option value="transit">Transit</option>
                  <option value="unknown">Unknown</option>
                  <option value="returned">Returned</option>
                  <option value="return-delivered">Return delivered</option>
                </select>
              </div>
            </Row>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <div className="text-end t-flex t-flex-wrap t-gap-2 t-justify-end">
              <Btn
                attrBtn={{
                  className: 'mr-3',
                  color: 'primary',
                  disabled: !selectedRows?.length || !ingredientPrices || !focusAreas || isDownloadingPdf,
                  onClick: downloadAllSelectedOrders,
                }}
              >
                {isDownloadingPdf ? 'Generating PDFs...' : 'Download'}
              </Btn>
              <Btn
                attrBtn={{
                  className: 'mr-3',
                  color: 'primary',
                  disabled: !selectedRows?.length || !ingredientPrices || isDownloadingLabel,
                  onClick: downloadAllSelectedLabels,
                }}
              >
                {isDownloadingLabel ? 'Generating Label...' : 'Label'}
              </Btn>
              <Btn
                attrBtn={{
                  className: 'mr-2',
                  color: 'secondary',
                  disabled: !selectedRows?.length || isDownloadingCsv,
                  onClick: downloadWeightCalculationsCSV,
                }}
              >
                {isDownloadingCsv ? 'Generating CSV...' : 'Download Weight CSV'}
              </Btn>
              <Btn
                attrBtn={{
                  className: 'mr-2',
                  color: 'info',
                  disabled: !selectedRows?.length || isDownloadingSummary,
                  onClick: downloadSummaryOrdersCSV,
                }}
              >
                {isDownloadingSummary ? 'Generating Summary...' : 'Download Summary Orders'}
              </Btn>
              <Btn
                attrBtn={{
                  className: 'mr-2',
                  color: 'info',
                  disabled: !selectedRows?.length,
                  onClick: createShipment,
                }}
              >
                Shipment
              </Btn>
              <Btn
                attrBtn={{
                  className: 'mr-2',
                  color: 'info',
                  disabled: !selectedRows?.length,
                  onClick: getShipmentTracking,
                }}
              >
                Get Tracking
              </Btn>
              <Btn
                attrBtn={{
                  className: 'mr-2',
                  color: 'info',
                  disabled: !selectedRows?.length || sendEmailUpdatesLoading,
                  onClick: toggleEmailTemplateModal,
                }}
              >
                Send Email Updates
              </Btn>
              <Btn
                attrBtn={{
                  className: 'mr-2',
                  color: 'primary',
                  disabled: !selectedRows?.length || updateOrderStatusAndDetailsLoading,
                  onClick: toggleUpdateStatusModal,
                }}
              >
                {updateOrderStatusAndDetailsLoading ? 'Updating...' : 'Update Status'}
              </Btn>
            </div>

            {!isLoading && (
              <div className="my-3 text-muted">
                Showing {data.length} of {totalRows} orders
              </div>
            )}

            <DataTable
              columns={tableColumns}
              data={data}
              expandableRows
              expandableRowsComponent={OrderItemsExpanded}
              expandableRowDisabled={(row) => !Array.isArray(row?.order_items) || row.order_items.length === 0}
              striped={true}
              center={true}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              progressPending={isLoading}
              selectableRows
              //   selectableRowsSingle
              selectableRowsHighlight
              onSelectedRowsChange={handleRowSelected}
              clearSelectedRows={toggleSelect}
              paginationPerPage={pageSize}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              paginationComponentOptions={{
                noRowsPerPage: false, // Hide the dropdown that allows changing the number of rows per page
              }}
            />
          </CardBody>
        </Card>

        <Row>
          <Col sm="8">
            <Card>
              <CardBody className="personalized-formula">
                <div className="table-responsive">
                  <table className="table accordion" id="accordionExample">
                    <thead>
                      <tr>
                        <th scope="col" className="col-6">
                          Nutrient
                        </th>
                        <th scope="col">Quantity</th>
                        <th scope="col">% NRV</th>
                        <th scope="col">Daily Cogs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ingredients &&
                        ingredients.map((item) => {
                          return (
                            <tr key={item.id}>
                              <td>{item.ingredient}</td>
                              <td className="t-flex t-gap-2 t-items-center">
                                <input
                                  type="number"
                                  value={editedIngredients[order?.id]?.[item.id] || item.daily_dose || ''}
                                  onChange={(e) => handleIngredientEdit(item.id, e.target.value)}
                                  className="form-control form-control-sm"
                                  style={{ width: '80px' }}
                                  placeholder="0"
                                />
                                {editedIngredients[order?.id]?.[item.id] && (
                                  <span className="ml-1 text-xs text-blue-600">●</span>
                                )}
                              </td>
                              <td>{item.per_eu_nrv === 0 ? '*' : `${item.per_eu_nrv}%`}</td>
                              <td>{item?.cogs ? item.cogs : 0}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col sm="4">
            <Card>
              <CardBody>
                <h5 className="t-mb-2 t-font-semibold">Order Notes</h5>
                {order?.notes && order.notes.length > 0 ? (
                  <ul className="list-group">
                    {order.notes.map((note) => (
                      <li
                        key={note.id}
                        className="list-group-item d-flex justify-content-between align-items-start"
                      >
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2">
                            <span>{note.note}</span>
                            {note.isCompleted === 1 && (
                              <i className="fa fa-check-circle text-success" title="Completed"></i>
                            )}
                          </div>
                        </div>
                        {note.isCompleted === 0 && (
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleMarkOrderNoteComplete(note.id)}
                            disabled={markingCompleteNoteId === note.id}
                            style={{ opacity: markingCompleteNoteId === note.id ? 0.5 : 1 }}
                            title="Mark as Complete"
                          >
                            <i className="fa fa-check"></i>
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted">No notes available</p>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
        {questionsAnswers && personalInfo && (
          <Card>
            <CardBody>
              <Row style={{ gap: 12 }}>
                <h3>Quiz Question Answers</h3>
                {Object.keys(personalInfo).map((item, i) => (
                  <div key={'personal-info-' + i} className="">
                    <div
                      dangerouslySetInnerHTML={{ __html: item }}
                      style={{
                        fontWeight: 500,
                        fontSize: '15px',
                        textTransform: 'capitalize',
                      }}
                    />
                    <div
                      dangerouslySetInnerHTML={{
                        __html: personalInfo[item],
                      }}
                    />
                  </div>
                ))}
                {questionsAnswers.slice(1, questionsAnswers.length).map((item, i) => (
                  <div key={'qa-' + i} className="">
                    <div
                      dangerouslySetInnerHTML={{ __html: item.question }}
                      style={{
                        fontWeight: 500,
                        fontSize: '15px',
                      }}
                    />
                    <div dangerouslySetInnerHTML={{ __html: formatQuizAnswer(item.answer) }} />
                  </div>
                ))}
              </Row>
            </CardBody>
          </Card>
        )}
        <ToastContainer />
        <Modal isOpen={emailTemplateModalOpen} toggle={toggleEmailTemplateModal}>
          <ModalHeader toggle={toggleEmailTemplateModal}>Send Email</ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <label htmlFor="emailTemplateSelect" className="form-label fw-bold">
                Email Template
              </label>
              <select
                id="emailTemplateSelect"
                className="form-select"
                value={selectedEmailTemplate}
                onChange={(e) => setSelectedEmailTemplate(e.target.value)}
              >
                {EMAIL_TEMPLATES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-3">
              <p className="mb-2">
                Are you sure you want to send the email{' '}
                <strong>"{EMAIL_TEMPLATES.find((t) => t.value === selectedEmailTemplate)?.label}"</strong> to the
                following {selectedRows.length} order(s)?
              </p>
              <ul className="list-unstyled ms-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {selectedRows.map((row) => (
                  <li key={row.id}>
                    • Order #{row.id}
                    {row.name ? ` — ${row.name}` : ''}
                    {row.email ? ` (${row.email})` : ''}
                  </li>
                ))}
              </ul>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleEmailTemplateModal} disabled={sendEmailUpdatesLoading}>
              Cancel
            </Button>
            <Button color="primary" onClick={sendEmailUpdatesSubmit} disabled={sendEmailUpdatesLoading}>
              {sendEmailUpdatesLoading ? 'Sending...' : 'Send'}
            </Button>
          </ModalFooter>
        </Modal>
        <Modal isOpen={updateStatusModalOpen} toggle={toggleUpdateStatusModal}>
          <ModalHeader toggle={toggleUpdateStatusModal}>Update Order Status</ModalHeader>
          <ModalBody>
            <p className="mb-3">Are you sure you want to update the status of the selected orders?</p>
            <div className="mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-control"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="">Select status</option>
                <option value="pending">Pending</option>
                <option value="Ready to dispatch">Ready to dispatch</option>
                <option value="dispatched">Dispatched</option>
                <option value="delivered">Delivered</option>
                <option value="failure">Failure</option>
                <option value="pre-transit">Pre transit</option>
                <option value="transit">Transit</option>
                <option value="unknown">Unknown</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Details</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter details (optional)"
                value={statusDetails}
                onChange={(e) => setStatusDetails(e.target.value)}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleUpdateStatusModal} disabled={updateOrderStatusAndDetailsLoading}>
              Cancel
            </Button>
            <Button color="primary" onClick={handleConfirmUpdateStatus} disabled={updateOrderStatusAndDetailsLoading || !selectedStatus}>
              {updateOrderStatusAndDetailsLoading ? 'Updating...' : 'Confirm'}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </Fragment>
  );
};

export default OrdersPdf;
